import type { CenterProps, MotionPropsFor } from '@hover/blueprint';
import { IconButton, motion } from '@hover/blueprint';
import { iconSaveFilled, iconSaveOutlined } from '@hover/icons';
import { AnimatePresence } from 'framer-motion';
import React from 'react';

import { useReferringProject } from 'src/modules/homeownerApp/components/Customize/useReferringProject';
import { imageIsAddedToAnyProject } from 'src/modules/homeownerApp/components/Utils';
import { useBlurFade } from 'src/modules/homeownerApp/helpers';
import { useSaveToProject } from 'src/modules/homeownerApp/hooks';
import { isDisplay } from 'src/modules/homeownerApp/types';
import type {
  AiDisplayImageFragment,
  AiGeneratedImageFragment,
} from 'src/shared/graphql/generated';
import { fadePresence } from 'src/shared/utils/motion';

import { useCustomization } from '../useCustomization';

interface BookmarkButtonProps extends MotionPropsFor<CenterProps> {
  /**
   * The image to store in the project.
   */
  image: AiGeneratedImageFragment | AiDisplayImageFragment | undefined | null;
  /**
   * Flag to control the visiblity of the button.
   *
   * @defaultValue true
   */
  isVisible?: boolean;
}

const MotionIconButton = motion(IconButton);

const BookmarkButton = ({ image, isVisible = true }: BookmarkButtonProps) => {
  const { projects, referreringProject, loading } = useReferringProject();

  const { handleSave } = useSaveToProject({
    image,
    location: 'customize',
    referreringProject,
  });
  const { isCustomizing } = useCustomization();

  const isPresent = isVisible && !isCustomizing && !loading;
  const blurFade = useBlurFade(isPresent);

  if (!image) return null;

  const isBookmarked = isDisplay(image)
    ? imageIsAddedToAnyProject(projects, image.aiGeneratedImage.id)
    : imageIsAddedToAnyProject(projects, image.id);

  return (
    <AnimatePresence initial={false}>
      {isPresent && (
        <MotionIconButton
          borderRadius="350"
          bottom={{ base: 400, tablet: 600 }}
          colorScheme="light"
          icon={isBookmarked ? iconSaveFilled : iconSaveOutlined}
          label={isBookmarked ? 'saved' : 'save'}
          onClick={handleSave}
          position="absolute"
          right={{ base: 400, tablet: 600 }}
          sx={blurFade}
          transition={{ duration: 0.3 }}
          variant="glass"
          zIndex={2}
          {...fadePresence}
        />
      )}
    </AnimatePresence>
  );
};

export { BookmarkButton };
